/**
 * Input Capture Manager
 *
 * Generic manager for capturing browser input events:
 * - Keyboard (keydown, keyup)
 * - Mouse (mousedown, mouseup, mousemove, wheel)
 * - Touch (touchstart, touchend, touchmove, touchcancel)
 * - Gamepad (polling via RAF)
 *
 * Provides unified interface for input handling without relying on
 * global stores or React components. Pure TypeScript, testable in isolation.
 *
 * This is a generic utility that can be used by any feature.
 */

export interface InputAccessRights {
  keyboard?: boolean;
  mouse?: boolean;
  touch?: boolean;
  gamepad?: boolean;
}

export interface InputCaptureConfig {
  /**
   * Which input types to capture
   */
  access: InputAccessRights;

  /**
   * Handler for keyboard events
   */
  onKeyboard?: (event: KeyboardEvent) => void;

  /**
   * Handler for mouse events
   */
  onMouse?: (event: MouseEvent) => void;

  /**
   * Handler for touch events
   */
  onTouch?: (event: TouchEvent) => void;

  /**
   * Handler for gamepad events (receives all connected gamepads)
   */
  onGamepad?: (gamepads: (Gamepad | null)[]) => void;

  /**
   * Debug callback to track when events are captured
   */
  onDebugCapture?: (inputType: string, eventType: string) => void;
}

/**
 * Manager for browser input capture
 * Handles keyboard, mouse, touch, and gamepad input with automatic cleanup
 */
export class InputCaptureManager {
  private config: InputCaptureConfig;
  private isActive = false;
  private cleanupFunctions: (() => void)[] = [];
  private gamepadPollingRAF?: number;
  private lastGamepadInputTime = 0;

  constructor(config: InputCaptureConfig) {
    this.config = config;
  }

  /**
   * Start capturing input
   * Sets up event listeners based on access rights
   */
  public start(): void {
    if (this.isActive) {
      console.warn('[InputCaptureManager] Already active, ignoring start()');
      return;
    }

    const { access } = this.config;

    // Keyboard
    if (access.keyboard && this.config.onKeyboard) {
      // Use capture phase so we still see events even if some UI stops propagation.
      // (This can happen with embedded players / overlays.)
      const options: AddEventListenerOptions = { capture: true };
      this.addListener(window, 'keydown', this.handleKeyboard, options);
      this.addListener(window, 'keyup', this.handleKeyboard, options);
    }

    // Mouse
    // Always register mouse handlers (including on touch-capable desktops).
    // Some Windows machines report `navigator.maxTouchPoints > 0` even when using a normal mouse.
    // We avoid double-input by filtering *synthetic* mouse events in `handleMouse`.
    if (access.mouse && this.config.onMouse) {
      // Use capture phase so we still see events even if some UI stops propagation.
      // (This can happen with embedded players / overlays.)
      const options: AddEventListenerOptions = { capture: true, passive: false };
      this.addListener(window, 'mousedown', this.handleMouse, options);
      this.addListener(window, 'mouseup', this.handleMouse, options);
      this.addListener(window, 'mousemove', this.handleMouse, options);
      // Wheel can scroll the page and steal focus if we fail to preventDefault; keep it non-passive.
      this.addListener(window, 'wheel', this.handleMouse, options);
    }

    // Touch
    if (access.touch && this.config.onTouch) {
      this.addListener(window, 'touchstart', this.handleTouch);
      this.addListener(window, 'touchend', this.handleTouch);
      this.addListener(window, 'touchmove', this.handleTouch);
      this.addListener(window, 'touchcancel', this.handleTouch);
    }

    // Gamepad (poll-based)
    if (access.gamepad && this.config.onGamepad) {
      this.startGamepadPolling();
    }

    this.isActive = true;
  }

  /**
   * Stop capturing input
   * Removes all event listeners and stops gamepad polling
   */
  public stop(): void {
    if (!this.isActive) {
      return;
    }

    this.stopGamepadPolling();
    this.cleanupFunctions.forEach((cleanup) => cleanup());
    this.cleanupFunctions = [];
    this.isActive = false;
  }

  /**
   * Check if currently capturing input
   */
  public isCapturing(): boolean {
    return this.isActive;
  }

  /**
   * Cleanup all resources
   */
  public cleanup(): void {
    this.stop();
  }

  /**
   * Add event listener and track cleanup
   */
  private addListener(target: EventTarget, event: string, handler: EventListener, options?: AddEventListenerOptions): void {
    target.addEventListener(event, handler, options);
    this.cleanupFunctions.push(() => {
      target.removeEventListener(event, handler, options);
    });
  }

  /**
   * Keyboard event handler
   */
  private handleKeyboard = (event: Event): void => {
    // Prevent default browser behaviors (e.g., scrolling, form submission)
    event.preventDefault();
    const keyboardEvent = event as KeyboardEvent;
    this.config.onDebugCapture?.('keyboard', keyboardEvent.type);
    this.config.onKeyboard?.(keyboardEvent);
  };

  /**
   * Mouse event handler
   */
  private handleMouse = (event: Event): void => {
    const mouseEvent = event as MouseEvent;
    const target = mouseEvent.target as HTMLElement;

    if (this.isGamepadOverlayElement(target)) {
      return;
    }

    // Prevent default browser behaviors (e.g., text selection, drag-and-drop)
    event.preventDefault();
    this.config.onDebugCapture?.('mouse', mouseEvent.type);
    this.config.onMouse?.(mouseEvent);
  };

  /**
   * Touch event handler
   */
  private handleTouch = (event: Event): void => {
    const touchEvent = event as TouchEvent;
    const target = touchEvent.target as HTMLElement;

    if (this.isGamepadOverlayElement(target)) {
      return;
    }

    const isInteractiveElement = this.isInteractiveElement(target);

    if (touchEvent.type === 'touchmove') {
      // Defense-in-depth: try to prevent touchmove (will be ignored in passive listener, but CSS handles it)
      event.preventDefault();
    } else if (!isInteractiveElement) {
      // Defense-in-depth: prevent touchstart/touchend on non-interactive elements
      event.preventDefault();
    }
    // else: allow touchstart/touchend on interactive elements (buttons work!)

    this.config.onDebugCapture?.('touch', touchEvent.type);
    this.config.onTouch?.(touchEvent);
  };

  /**
   * Check if the element belongs to the mobile gamepad overlay.
   * Gamepad elements are marked with `data-gamepad-input` (on DraggableWrapper and ButtonMenu).
   * Touches on these elements are already handled by MobileGamepadInputStandalone
   * and must not be forwarded to the game stream.
   */
  private isGamepadOverlayElement(element: HTMLElement | null): boolean {
    if (!element) return false;

    let current = element;
    let depth = 0;
    const maxDepth = 10;

    while (current && depth < maxDepth) {
      if (current.hasAttribute('data-gamepad-input')) {
        return true;
      }
      current = current.parentElement as HTMLElement;
      depth++;
    }

    return false;
  }

  /**
   * Check if an element is interactive (button, link, input, etc.) or has pointer-events-auto
   * These elements should receive their normal touch events
   */
  private isInteractiveElement(element: HTMLElement | null): boolean {
    if (!element) return false;

    // Check the element and all its parents up to 10 levels
    let current = element;
    let depth = 0;
    const maxDepth = 10;

    while (current && depth < maxDepth) {
      // Check if element is inherently interactive
      const tagName = current.tagName?.toLowerCase();
      if (
        tagName === 'button' ||
        tagName === 'a' ||
        tagName === 'input' ||
        tagName === 'select' ||
        tagName === 'textarea' ||
        tagName === 'label'
      ) {
        return true;
      }

      // Check for pointer-events-auto class (explicitly marked as interactive)
      if (current.classList?.contains('pointer-events-auto')) {
        return true;
      }

      // Check for role attribute indicating interactivity
      const role = current.getAttribute('role');
      if (role === 'button' || role === 'link' || role === 'menuitem') {
        return true;
      }

      // Check for onclick attribute or data-* attributes suggesting interactivity
      if (current.hasAttribute('onclick') || current.hasAttribute('data-interactive')) {
        return true;
      }

      // Move up to parent
      current = current.parentElement as HTMLElement;
      depth++;
    }

    return false;
  }

  /**
   * Start gamepad polling using requestAnimationFrame
   */
  private startGamepadPolling(): void {
    const poll = () => {
      if (!this.isActive) return;

      const gamepads = navigator.getGamepads();

      // Check if there's actual input (throttle debug events)
      const hasInput = this.detectGamepadInput(gamepads);
      if (hasInput) {
        const now = Date.now();
        if (now - this.lastGamepadInputTime > 100) {
          this.config.onDebugCapture?.('gamepad', 'input');
          this.lastGamepadInputTime = now;
        }
      }

      // Always call handler (it may need to process state)
      this.config.onGamepad?.(Array.from(gamepads));

      this.gamepadPollingRAF = requestAnimationFrame(poll);
    };

    this.gamepadPollingRAF = requestAnimationFrame(poll);
  }

  /**
   * Stop gamepad polling
   */
  private stopGamepadPolling(): void {
    if (this.gamepadPollingRAF !== undefined) {
      cancelAnimationFrame(this.gamepadPollingRAF);
      this.gamepadPollingRAF = undefined;
    }
  }

  /**
   * Detect if any gamepad has meaningful input
   * Used to throttle debug events
   */
  private detectGamepadInput(gamepads: (Gamepad | null)[]): boolean {
    for (const gamepad of gamepads) {
      if (!gamepad) continue;

      // Check buttons
      for (const button of gamepad.buttons) {
        if (button.pressed) return true;
      }

      // Check axes (deadzone of 0.1)
      for (const axis of gamepad.axes) {
        if (Math.abs(axis) > 0.1) return true;
      }
    }

    return false;
  }
}

